import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentPortalMode } from '@/hooks/useCurrentPortalMode';
import { useAuth } from '@/hooks/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, AlertCircle } from 'lucide-react';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { PlansDialog } from '@/components/billing/PlansDialog';
import { CheckoutPaymentDialog } from '@/components/billing/CheckoutPaymentDialog';
import { SubscriptionDowngradeDialog } from '@/components/billing/SubscriptionDowngradeDialog';
import { GenericPageHero } from '@/components/shared/GenericPageHero';
import { PageShell, SectionLoader } from '@/components/ui/progressive-loading';
import { RoleCard } from '@/components/billing/RoleCard';
import { CompanyBillingSection } from '@/components/billing/CompanyBillingSection';
import { BillingCompanySelector } from '@/components/billing/BillingCompanySelector';
import { CreditTransactionDebug } from '@/components/billing/CreditTransactionDebug';
import { useAllCompanyBilling } from '@/hooks/useAllCompanyBilling';
import { supabase } from '@/integrations/supabase/client';
import type { CompanyBillingData, CompanySubscriptionTier } from '@/types/billing';
import { useTranslation } from '@/hooks/useTranslation';

export default function BillingPage() {
  const {
    isReady,
    hasTalentAccess,
    hasRecruiterAccess,
    hasInternalAccess,
    hasCompanyAccess,
    hasNannyAccess,
    hasMotherAccess
  } = useAuth();
  const currentMode = useCurrentPortalMode();
  const isInNannyMotherPlatform = currentMode === 'nanny' || currentMode === 'mother';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useTranslatedToast();

  // Don't start loading billing data until auth is ready
  const {
    loading,
    error,
    subscriptionTiers,
    companiesBillingData,
    refetch: refetchBilling
  } = useAllCompanyBilling();

  // Performance monitoring
  React.useEffect(() => {
    console.debug('[BillingPage] Rendered', {
      timestamp: Date.now(),
      isReady,
      loading,
      error,
      companiesCount: companiesBillingData.length,
      subscriptionTiersCount: subscriptionTiers.length
    });
  });

  const [showPlansDialog, setShowPlansDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const [selectedCompanyForPlans, setSelectedCompanyForPlans] = useState<CompanyBillingData | null>(null);
  const [pendingPlanSelection, setPendingPlanSelection] = useState<{
    tier: CompanySubscriptionTier;
    totalPrice: number;
  } | null>(null);
  const [pendingFreeTier, setPendingFreeTier] = useState<CompanySubscriptionTier | null>(null);
  const [isProcessingDowngrade, setIsProcessingDowngrade] = useState(false);

  const handlePlanSelected = async (tier: CompanySubscriptionTier) => {
    console.log('Plan selected:', tier);
    
    if (!selectedCompanyForPlans) {
      toast.raw.error('No company selected');
      return;
    }
    
    const currentTierKey = selectedCompanyForPlans.tier?.tier_key;
    const isSelectingFree = tier.tier_key === 'free';
    const isCurrentlyOnFree = currentTierKey === 'free';
    const isDowngradingToFree = isSelectingFree && !isCurrentlyOnFree;
    const isTemporaryPlan = selectedCompanyForPlans.subscription?.is_temporary_plan;
    const hasStripeSubscription = !!selectedCompanyForPlans.subscription?.stripe_subscription_id;
    
    // Check if already on this plan (but allow upgrading from Welcome Gift)
    if (currentTierKey === tier.tier_key && !isTemporaryPlan && hasStripeSubscription) {
      toast.raw.info(t('pages:billing.already_on_plan'));
      setShowPlansDialog(false);
      return;
    }
    
    // If user is on Welcome Gift and selecting the same tier, allow upgrade to paid
    if (currentTierKey === tier.tier_key && isTemporaryPlan) {
      console.log('Upgrading from Welcome Gift to paid subscription for tier:', tier.tier_key);
      // Continue to payment dialog below
    }
    
    // If downgrading to free from paid plan, show confirmation dialog
    if (isDowngradingToFree) {
      setPendingFreeTier(tier);
      setShowPlansDialog(false);
      setShowDowngradeDialog(true);
      return;
    }
    
    // If switching to paid plan, show payment dialog
    if (!isSelectingFree) {
      setPendingPlanSelection({
        tier,
        totalPrice: tier.monthly_price
      });
      setShowPlansDialog(false);
      setShowPaymentDialog(true);
      return;
    }
    
    // Edge case: already on free, selecting free again
    toast.raw.info(t('pages:billing.already_on_plan'));
    setShowPlansDialog(false);
  };

  const handleDowngradeConfirm = async () => {
    if (!pendingFreeTier || !selectedCompanyForPlans) return;
    
    setIsProcessingDowngrade(true);
    
    try {
      console.log('Canceling subscription for company:', selectedCompanyForPlans.company.id);
      
      // Call edge function to cancel subscription (only cancels Stripe, webhook handles the rest)
      const { data, error } = await supabase.functions.invoke(
        'cancel-subscription',
        {
          body: {
            companyId: selectedCompanyForPlans.company.id,
            newTierKey: pendingFreeTier.tier_key
          }
        }
      );
      
      if (error) {
        console.error('Cancellation error:', error);
        throw new Error(error.message || 'Failed to cancel subscription');
      }
      
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to cancel subscription');
      }
      
      // Show processing message
      toast.raw.info(t('pages:billing.cancellation_processing'));
      
      // Close the dialog immediately but keep the loading state
      setShowDowngradeDialog(false);
      
      // Poll for subscription status update (webhook processing)
      const companyId = selectedCompanyForPlans.company.id;
      let attempts = 0;
      const maxAttempts = 15; // 15 seconds max wait
      
      const pollInterval = setInterval(async () => {
        attempts++;
        
        try {
          // Check if subscription has been updated to free tier
          const { data: updatedSub } = await supabase
            .from('company_subscriptions')
            .select('tier_key, status')
            .eq('company_id', companyId)
            .single();
          
          if (updatedSub?.tier_key === 'free' && updatedSub?.status === 'canceled') {
            // Webhook processing complete
            clearInterval(pollInterval);
            toast.raw.success(t('pages:billing.cancellation_success'));
            await refetchBilling();
            setIsProcessingDowngrade(false);
            setPendingFreeTier(null);
            setSelectedCompanyForPlans(null);
          } else if (attempts >= maxAttempts) {
            // Timeout - but likely still processing
            clearInterval(pollInterval);
            toast.raw.warning(t('pages:billing.cancellation_processing_long'));
            await refetchBilling();
            setIsProcessingDowngrade(false);
            setPendingFreeTier(null);
            setSelectedCompanyForPlans(null);
          }
        } catch (pollError) {
          console.error('Error polling subscription status:', pollError);
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setIsProcessingDowngrade(false);
            setPendingFreeTier(null);
            setSelectedCompanyForPlans(null);
          }
        }
      }, 1000); // Poll every 1 second
      
    } catch (error) {
      console.error('Error downgrading plan:', error);
      toast.raw.error(
        error instanceof Error 
          ? error.message 
          : t('pages:billing.cancellation_failed')
      );
      setIsProcessingDowngrade(false);
      setShowDowngradeDialog(false);
      setPendingFreeTier(null);
      setSelectedCompanyForPlans(null);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentDialog(false);
    setSelectedCompanyForPlans(null);
    setPendingPlanSelection(null);
    // Refetch billing data after successful payment
    refetchBilling();
  };

  const handlePaymentClose = () => {
    setShowPaymentDialog(false);
    setSelectedCompanyForPlans(null);
    setPendingPlanSelection(null);
  };

  const [selectedBillingCompanyId, setSelectedBillingCompanyId] = useState<string | null>(null);

  // Get the billing data for the selected billing company (independent of global company selection)
  const selectedCompanyBillingData = companiesBillingData.find(company => company.company.id === (selectedBillingCompanyId || companiesBillingData[0]?.company.id));

  // Set default selected company when companies load
  React.useEffect(() => {
    if (companiesBillingData.length > 0 && !selectedBillingCompanyId) {
      setSelectedBillingCompanyId(companiesBillingData[0].company.id);
    }
  }, [companiesBillingData, selectedBillingCompanyId]);

  // Handle error in useEffect to prevent re-renders
  React.useEffect(() => {
    if (error) {
      toast.raw.error(error);
    }
  }, [error, toast]);

  // Always show PageShell structure, handle loading inside sections
  return (
    <PageShell>
      <GenericPageHero icon={CreditCard} title="Billing" description={t('pages:billing.description')} />

      {(!isReady || loading) ? (
        <SectionLoader variant="grid" count={4} showTitle />
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{t('pages:billing.error_loading')} {error}</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {isInNannyMotherPlatform ? (
            // Nanny/Mother Platform Billing View
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('pages:billing.your_access')}</h2>
              
              {/* Nanny Role Section */}
              {hasNannyAccess && (
                <RoleCard
                  roleName={t('pages:billing.nanny', 'Nanny')}
                  description={t('pages:billing.nanny_description', 'Access to nanny job search and application management')}
                  variant="free"
                />
              )}

              {/* Mother Role Section */}
              {hasMotherAccess && (
                <RoleCard
                  roleName={t('pages:billing.mother', 'Parent/Mother')}
                  description={t('pages:billing.mother_description', 'Post nanny jobs and manage applications')}
                  variant="free"
                />
              )}

              {/* Coming Soon Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    {t('pages:billing.premium_features_coming_soon', 'Premium Features Coming Soon')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {t('pages:billing.nanny_platform_premium_description', 
                      'Enhanced features for the NannyCare platform including priority placement, background check services, and more will be available soon.'
                    )}
                  </p>
                </CardContent>
              </Card>

              {/* Link back to main platform billing if they have other access */}
              {(hasTalentAccess || hasCompanyAccess || hasRecruiterAccess || hasInternalAccess) && (
                <Card className="border-2 border-primary/20 bg-primary/5">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">
                          {t('pages:billing.main_platform_billing', 'Main Platform Billing')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {t('pages:billing.view_other_subscriptions', 'View your subscriptions for Evee talent, company, and recruiter features')}
                        </p>
                      </div>
                      <Button 
                        onClick={() => {
                          localStorage.setItem('workspace_mode', 'talent');
                          navigate('/workspace/talent/jobs');
                        }}
                        variant="outline"
                      >
                        {t('pages:billing.switch_to_main_platform', 'Switch to Main Platform')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <>
              {/* Company Billing Section */}
              {companiesBillingData.length > 0 && (
                <div className="space-y-6">
                  {/* Company Selector */}
                  <BillingCompanySelector
                    companies={companiesBillingData}
                    selectedCompanyId={selectedBillingCompanyId}
                    onSelectCompany={setSelectedBillingCompanyId}
                  />

                  {/* All company content - kept mounted, controlled by visibility */}
                  {companiesBillingData.map((companyData) => {
                    const isActive = selectedBillingCompanyId === companyData.company.id;
                    return (
                      <div
                        key={companyData.company.id}
                        className={isActive ? '' : 'hidden'}
                      >
                        <CompanyBillingSection 
                          companyData={companyData} 
                          subscriptionTiers={subscriptionTiers} 
                          onChangePlans={(company) => {
                            setSelectedCompanyForPlans(company);
                            setShowPlansDialog(true);
                          }}
                          onRefetchBilling={refetchBilling}
                          isActive={isActive}
                        />
                        
                        {/* Debug Component - Remove in production */}
                        {process.env.NODE_ENV === 'development' && isActive && (
                          <CreditTransactionDebug
                            companyId={companyData.company.id}
                            companyName={companyData.company.name}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Other Role Sections */}
              {(hasTalentAccess || hasRecruiterAccess || hasInternalAccess) && (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold">{t('pages:billing.your_access')}</h2>
                  
                  {/* Talent Role Section */}
                  {hasTalentAccess && (
                    <RoleCard
                      roleName={t('pages:billing.talent')}
                      description={t('pages:billing.talent_description')}
                      variant="free"
                    />
                  )}

                  {/* Recruiter Role Section */}
                  {hasRecruiterAccess && (
                    <RoleCard
                      roleName={t('pages:billing.recruiter')}
                      description={t('pages:billing.recruiter_description')}
                      variant="free"
                    />
                  )}

                  {/* Internal Role Section */}
                  {hasInternalAccess && (
                    <RoleCard
                      roleName={t('pages:billing.internal_admin')}
                      description={t('pages:billing.internal_admin_description')}
                      variant="admin"
                    />
                  )}
                </div>
              )}
            </>
          )}

          {/* Plans Dialog */}
          <PlansDialog
            open={showPlansDialog}
            onClose={() => setShowPlansDialog(false)}
            subscriptionTiers={subscriptionTiers}
            currentTier={selectedCompanyForPlans?.tier}
            onPlanSelected={handlePlanSelected}
            isTemporaryPlan={selectedCompanyForPlans?.subscription?.is_temporary_plan}
            hasStripeSubscription={!!selectedCompanyForPlans?.subscription?.stripe_subscription_id}
          />

          {/* Payment Dialog */}
          {pendingPlanSelection && selectedCompanyForPlans && (
            <CheckoutPaymentDialog
              isOpen={showPaymentDialog}
              onClose={handlePaymentClose}
              selectedTier={pendingPlanSelection.tier}
              currentTier={selectedCompanyForPlans.tier}
              companyData={selectedCompanyForPlans}
              onSuccess={handlePaymentSuccess}
            />
          )}

          {/* Downgrade Confirmation Dialog */}
          {showDowngradeDialog && pendingFreeTier && selectedCompanyForPlans && (
            <SubscriptionDowngradeDialog
              open={showDowngradeDialog}
              onClose={() => {
                setShowDowngradeDialog(false);
                setPendingFreeTier(null);
              }}
              onConfirm={handleDowngradeConfirm}
              currentTier={selectedCompanyForPlans.tier}
              newTier={pendingFreeTier}
              isLoading={isProcessingDowngrade}
            />
          )}
        </>
      )}
    </PageShell>
  );
}
